// Compatibility shim so ported per-bot pages keep importing `{ useTenant }`.
// The standalone hub UI has no gateway tenant/bot directory; the active bot is
// the `:botId` in the route (set by the bot bar in Layout), falling back to the
// BotContext for system-wide pages. This synthesizes a single-tenant/single-bot
// view so the ported pages' access checks pass and `bot.id` resolves to the
// botId the hub expects.
//
// IMPORTANT: the return is memoized by (id, system) — ported pages put
// tenants/tenantBots in useEffect deps, so fresh refs each render would loop.
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { useBot } from './BotContext';

export interface TenantBot {
  botId: string;
  system: string;
  botName?: string;
  id?: string;
}

export function useTenant() {
  const params = useParams<{ botId: string; system: string }>();
  const { system: ctxSystem, botId: ctxBotId } = useBot();
  const id = params.botId ?? ctxBotId ?? '';
  const system = params.system ?? ctxSystem;

  return useMemo(() => {
    const bot: TenantBot = { botId: id, system, botName: id, id };
    const tenantId = 'active';
    return {
      tenants: [{ id: tenantId, name: system, slug: system }],
      tenantBots: { [tenantId]: [bot] } as Record<string, TenantBot[]>,
      tenantPages: {} as Record<string, unknown[]>,
      selectedTenantId: tenantId,
      selectedPageId: null as string | null,
      loading: false,
      error: null as string | null,
      dispatch: () => {},
      refetch: () => {},
    };
  }, [id, system]);
}
